import ChatList from "./ChatList";
import Sidebar from "./Sidebar";
import { DataState } from "../context/DataProvider";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { BiSidebar } from "react-icons/bi";
import AutoResizableTextarea from "./AutoResizableTextarea";
import Tabbar from "./base/Tabbar";
import Integration from "./Integration";
import { FiLink } from "react-icons/fi";
import Steps from "./Steps";
import ReactMarkdown from 'react-markdown';
import IntegrationTutorial from "./IntegrationTutorial";
import toast from "react-hot-toast";
import { uploadFiles, uploadImages } from "../services/api";
import RightSideActionTracker from "./chat/RightSideActionTracker";
import { useNotifications } from "../context/NotificationProvider";
import { ProcessedContent } from "./meeting/ProcessedMeetingContent"
import { CheckCircle, XCircle, User, Calendar, Tag, FileText, AlertTriangle, DollarSign, ArrowRight, Link as LinkIcon, AtSign, ThumbsUp, ThumbsDown, GitBranch, X as XIcon, Braces, Brackets, Send, Users, Hourglass, Check, Clipboard, ListChecks, RefreshCw, FileX, AlertCircle, Loader2, Info, ChevronsRight, CheckSquare, AlertOctagon, ArrowRightCircle, Target, GitCommit, UserCheck, GitPullRequest, Paperclip, ChevronDown, ChevronRight, MessageSquare, Zap, Briefcase, Bookmark, Flag, MessageSquarePlus, X, Clock, MapPin, AlignLeft, Layers } from 'lucide-react';

import { getMeetingDetails, getUserConfiguredMeetingDetails, submitWorkflowApproval } from "../services/workflowApi";
import { appImages, notification } from "../utils/constants";
import FeedbackComponent from "./form/FeedbackComponent";
// import ExactLogo from "./Logo";
// import PlayLogo from "./Logo";
// import OqlousLogo from "./Logo";
import OqlousCTA from "./Logo";

// ✅ Custom Hook defined locally
const useOnClickOutside = (ref, handler, enabled = true) => {
  useEffect(() => {
    if (!enabled) return;
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler, enabled]);
};

// ✅ DetailItem Component
const DetailItem = ({ icon: Icon, label, children, className }) => {
  const type = typeof children;
  const isReactElement = React.isValidElement(children);
  const isPlainObject = typeof children === 'object' && children !== null && !Array.isArray(children) && !isReactElement && children.constructor === Object;
  const isArray = Array.isArray(children);

  const renderPrimitiveValue = () => {
    if (type === 'boolean') return <span className={`ml-2 font-mono font-semibold ${children ? 'text-emerald-400' : 'text-red-400'}`}>{children.toString()}</span>;
    if (type === 'number') return <span className="ml-2 font-mono text-sky-300">{children.toLocaleString()}</span>;
    if (type === 'string' || children === null || children === undefined) {
      return <span className="ml-2 text-zinc-300">{String(children)}</span>;
    }
    return null;
  };

  const renderJsonValue = (value, isArray) => {
    let jsonString;
    try { jsonString = JSON.stringify(value, null, 2); } catch (e) { jsonString = "[Error]"; }
    let isTruncated = false;
    if (jsonString.length > 1000) { jsonString = jsonString.substring(0, 1000); isTruncated = true; }
    return (
      <div className="w-full mt-2">
        <div className="flex items-center mb-1 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
          {isArray ? <Brackets className="h-3 w-3 mr-1.5" /> : <Braces className="h-3 w-3 mr-1.5" />}
          {isArray ? 'Array' : 'Object'}
        </div>
        <pre className="text-amber-300 bg-zinc-950/50 border border-zinc-800/50 px-3 py-2.5 rounded-lg text-[11px] whitespace-pre-wrap font-mono w-full overflow-x-auto custom-scrollbar shadow-inner">
          {jsonString}{isTruncated && '\n...'}
        </pre>
      </div>
    );
  };

  return (
    <div className={`flex items-start text-xs group ${className}`}>
      <div className="mt-0.5 mr-3 p-1 rounded-md bg-zinc-800/50 text-zinc-400 group-hover:bg-zinc-800 group-hover:text-zinc-300 transition-colors shrink-0">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <span className="mr-1 font-medium text-zinc-500">{label}:</span>
        {isPlainObject && renderJsonValue(children, false)}
        {isArray && renderJsonValue(children, true)}
        {isReactElement && <span className="ml-2">{children}</span>}
        {!isPlainObject && !isArray && !isReactElement && renderPrimitiveValue()}
      </div>
    </div>
  );
};

const JiraNotificationDetail = ({ notification }) => {
  const { payload: data } = notification.listener_data;
  return (
    <div className="h-full p-6 overflow-y-auto custom-scrollbar">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <DetailItem icon={User} label="Assignee">{data.assignee}</DetailItem>
        <DetailItem icon={Calendar} label="Due Date">{data.dueDate}</DetailItem>
        <DetailItem icon={AlertTriangle} label="Priority">{data.priority}</DetailItem>
        <DetailItem icon={Tag} label="Status">{data.status}</DetailItem>
      </div>
      <div className="pt-4 mt-4 border-t border-zinc-800">
        <p className="mb-2 text-xs font-semibold text-zinc-400">Description</p>
        <p className="p-4 text-sm border rounded-lg text-zinc-300 bg-zinc-800/30 border-zinc-700/50">{data.description}</p>
      </div>
      <a href={data.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-2 mt-6 text-xs font-medium text-blue-400 transition-colors border rounded-lg hover:text-blue-300 bg-[#5652CF]/10 border-[#5652CF]/20 hover:border-[#5652CF]/40">View in Jira <ArrowRight className="w-3 h-3 ml-2" /></a>
    </div>
  );
};

const GmailNotificationDetail = ({ notification }) => {
  const { payload: data } = notification.listener_data;
  return (
    <div className="h-full p-6 overflow-y-auto custom-scrollbar">
      <div className="pb-4 mb-4 space-y-3 text-xs border-b border-zinc-800">
        <div className="flex items-center gap-2 p-2 rounded-lg bg-zinc-800/30"><span className="w-10 font-semibold text-zinc-400">From:</span> <span className="text-zinc-200">{data.from}</span></div>
        <div className="flex items-center gap-2 p-2 rounded-lg bg-zinc-800/30"><span className="w-10 font-semibold text-zinc-400">To:</span> <span className="text-zinc-200">{data.to}</span></div>
      </div>
      <div className="p-4 prose-sm prose border prose-invert max-w-none text-zinc-300 bg-white/5 rounded-xl border-white/5" dangerouslySetInnerHTML={{ __html: data.body }} />
    </div>
  );
};

const ZohoCrmNotificationDetail = ({ notification }) => {
  const { payload: data } = notification.listener_data;
  return (
    <div className="h-full p-6 overflow-y-auto custom-scrollbar">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <DetailItem icon={Briefcase} label="Company">{data.leadName}</DetailItem>
        <DetailItem icon={User} label="Contact">{data.contactPerson}</DetailItem>
        <DetailItem icon={AtSign} label="Email">{data.contactEmail}</DetailItem>
        <DetailItem icon={DollarSign} label="Value">${data.dealSize.toLocaleString()}</DetailItem>
        <DetailItem icon={LinkIcon} label="Source">{data.source}</DetailItem>
      </div>
      <div className="pt-4 mt-4 border-t border-zinc-800">
        <p className="mb-2 text-xs font-semibold text-zinc-400">Notes</p>
        <p className="p-4 text-sm border rounded-lg text-zinc-300 bg-zinc-800/30 border-zinc-700/50">{data.notes}</p>
      </div>
      <a href={data.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-2 mt-6 text-xs font-medium text-blue-400 transition-colors border rounded-lg hover:text-blue-300 bg-[#5652CF]/10 border-[#5652CF]/20 hover:border-[#5652CF]/40">View in Zoho CRM <ArrowRight className="w-3 h-3 ml-2" /></a>
    </div>
  );
};

const SlackNotificationDetail = ({ notification }) => {
  const { payload: data } = notification.listener_data;
  return (
    <div className="h-full p-6 overflow-y-auto custom-scrollbar">
      <div className="p-5 border rounded-xl bg-zinc-800/40 border-zinc-700/50">
        <div className="flex items-center mb-3">
          <div className="flex items-center justify-center w-8 h-8 mr-3 rounded-full bg-zinc-700">
            <User className="w-4 h-4 text-zinc-300" />
          </div>
          <span className="text-sm font-bold text-zinc-200">{data.user}</span>
        </div>
        <p className="text-sm leading-relaxed text-zinc-300">{data.message}</p>
      </div>
      <a href={data.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-2 mt-6 text-xs font-medium text-blue-400 transition-colors border rounded-lg hover:text-blue-300 bg-[#5652CF]/10 border-[#5652CF]/20 hover:border-[#5652CF]/40">View in Slack <ArrowRight className="w-3 h-3 ml-2" /></a>
    </div>
  );
};

const ToolApprovalCard = ({ toolId, toolData, onDecision, onReasonChange, decision = {} }) => {
  const targetAppIcon = appImages[toolData.app];
  let parsedArguments = {};
  if (toolData.arguments) {
    if (typeof toolData.arguments === 'object' && toolData.arguments !== null) {
      parsedArguments = toolData.arguments;
    } else if (typeof toolData.arguments === 'string' && toolData.arguments.trim()) {
      try { parsedArguments = JSON.parse(toolData.arguments); } catch (error) { console.error("Error", error); }
    }
  }
  return (
    <div className={`p-4 rounded-xl transition-all duration-300 border shadow-sm ${decision.approved === true ? 'bg-emerald-950/20 border-emerald-800/50 shadow-emerald-900/10' : decision.approved === false ? 'bg-red-950/20 border-red-800/50 shadow-red-900/10' : 'bg-zinc-800/30 border-zinc-700/50'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center mb-3 text-sm">
            <div className="p-1 mr-2 bg-white rounded-md"><img src={targetAppIcon} alt={toolData.app} className="w-3 h-3" /></div>
            <span className="font-bold capitalize text-zinc-100">{toolData.name}</span>
          </div>
          <div className="pl-1 space-y-2 border-l-2 border-zinc-700">
            {Object.keys(parsedArguments).length > 0 ? (Object.entries(parsedArguments).map(([key, value]) => (<DetailItem icon={GitBranch} label={key.replace(/_/g, ' ')} key={key}>{value}</DetailItem>))) : (<p className="pl-2 text-xs italic text-zinc-500">No arguments provided.</p>)}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3 pt-4 mt-4 border-t border-zinc-700/30">
        <div className="flex items-center gap-3">
          <button onClick={() => onDecision(toolId, true, toolData.arguments)} className={`px-4 py-2 text-xs font-semibold rounded-lg flex items-center gap-2 transition-all flex-1 justify-center ${decision.approved === true ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/20' : 'bg-zinc-700 hover:bg-emerald-600/80 text-zinc-200'}`}><ThumbsUp className="h-3.5 w-3.5" /> Approve</button>
          <button onClick={() => onDecision(toolId, false, toolData.arguments)} className={`px-4 py-2 text-xs font-semibold rounded-lg flex items-center gap-2 transition-all flex-1 justify-center ${decision.approved === false ? 'bg-red-600 text-white shadow-lg shadow-red-900/20' : 'bg-zinc-700 hover:bg-red-600/80 text-zinc-200'}`}><ThumbsDown className="h-3.5 w-3.5" /> Deny</button>
        </div>
        {decision.approved === false && (<input type="text" placeholder="Reason for denial (required)" value={decision.disapprovalReason || ""} onChange={(e) => onReasonChange(toolId, e.target.value)} className="w-full px-3 py-2 text-xs transition border rounded-lg border-zinc-700 bg-zinc-900/50 text-zinc-200 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500" />)}
      </div>
    </div>
  );
};

const ApprovalNotificationDetail = ({ notification, onClose, onReadyStateChange }) => {
  const { workflow_data: data } = notification;
  const { userGoogleId: accessToken } = DataState();
  const { removeNotification } = useNotifications();
  const [toolDecisions, setToolDecisions] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleToolDecision = (toolId, approved, toolArguments) => { setToolDecisions((prev) => ({ ...prev, [toolId]: { arguments: toolArguments, approved, disapproved: !approved, disapprovalReason: approved ? "" : (prev[toolId]?.disapprovalReason || "") } })); };
  const handleReasonChange = (toolId, value) => { setToolDecisions((prev) => ({ ...prev, [toolId]: { ...prev[toolId], disapprovalReason: value } })); };
  const isApprovalReady = useMemo(() => {
    if (data.node_type !== 'action') return false;
    const tools = data.approvalNeededTools;
    return Object.keys(tools).every((toolId) => {
      const decision = toolDecisions[toolId];
      if (!decision) return false;
      if (decision.approved) return true;
      return decision.disapproved && decision.disapprovalReason?.trim() !== "";
    });
  }, [data, toolDecisions]);

  const handleSubmit = useCallback(async () => {
    if (!isApprovalReady) { toast.error("Please make a decision for all tools and provide reasons for denials."); return; }
    setIsLoading(true);
    const { execution_id, node_id } = data;
    try {
      await submitWorkflowApproval(accessToken, execution_id, node_id, toolDecisions);
      toast.success("Approval submitted successfully!");
      removeNotification(notification.id);
      onClose();
    } catch (error) { toast.error(typeof error === "string" ? error : error?.detail || error?.message || "Error"); } finally { setIsLoading(false); }
  }, [accessToken, data, isApprovalReady, notification.id, onClose, removeNotification, toolDecisions]);

  useEffect(() => { onReadyStateChange({ isReady: isApprovalReady, isLoading: isLoading, submit: handleSubmit }); }, [isApprovalReady, isLoading, handleSubmit, onReadyStateChange]);

  const renderActionApproval = () => (
    <div>
      <div className="p-4 mb-6 border shadow-sm rounded-xl bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 border-zinc-700/50"><h4 className="flex items-center gap-2 text-sm font-semibold text-zinc-200"><Zap className="w-4 h-4 text-amber-400" /> {data.node_description || data.node_title}</h4></div>
      <div className="space-y-4">{Object.entries(data.approvalNeededTools).map(([toolId, toolData]) => (<ToolApprovalCard key={toolId} toolId={toolId} toolData={toolData} onDecision={handleToolDecision} onReasonChange={handleReasonChange} decision={toolDecisions[toolId]} />))}</div>
    </div>
  );

  const renderHumanApproval = () => (<div className="p-4 space-y-4 border rounded-xl bg-zinc-800/30 border-zinc-700/50">{Object.entries(data.details).map(([key, value]) => (<div key={key} className="flex items-start text-sm"><span className="w-1/3 font-semibold capitalize text-zinc-400">{key.replace(/_/g, ' ')}:</span><span className="w-2/3 px-2 py-1 border rounded text-zinc-200 bg-zinc-900/50 border-zinc-800">{value}</span></div>))}</div>);

  return (
    <div className="h-full p-6 overflow-y-auto custom-scrollbar">
      <p className="flex items-center gap-2 mb-5 text-xs font-medium tracking-wide uppercase text-zinc-500">
        <GitCommit className="w-3 h-3" /> Workflow: <span className="text-zinc-300">{data.workflow_title}</span>
      </p>
      {data.node_type === 'action' && renderActionApproval()}
      {data.node_type === 'human' && renderHumanApproval()}
    </div>
  );
};

const parseUTCDateString = (dateInput) => {
  if (dateInput instanceof Date) return dateInput;
  if (typeof dateInput !== 'string' || !dateInput) return new Date(null);
  const hasOffset = dateInput.endsWith('Z') || dateInput.slice(-6).includes('+') || dateInput.slice(-6).includes('-');
  const correctedString = hasOffset ? dateInput : dateInput + 'Z';
  return new Date(correctedString);
};

// ✅ SUPER COMPACT: Footer Control Panel
const MeetingSubmissionControls = ({ notification, processedData, transcriptContent, onClose, onStageChat }) => {
  const { isBusy } = DataState();
  const [selections, setSelections] = useState({
    includeTranscript: true, overall_summary: true, member_wise_updates: true, task_wise_summary: true, unique_blockers: true, all_follow_ups: true, extracted_action_items: true, next_steps: true,
  });

  const handleSelectionChange = (e) => {
    const { name, checked } = e.target;
    setSelections(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = () => {
    if (isBusy) return;
    const payloadToSend = {
      event_id: notification.id, meeting_id: notification.meeting_id, source_type: notification.source_type, app: "microsoft_teams", event_name: notification.event_name, timestamp: notification.timestamp, subject: notification.subject, organizer: notification.organizer, attendees: notification.attendees, start: notification.start, end: notification.end, description: notification.bodyPreview, transcript_status: notification.transcript_status,
    };
    if (selections.includeTranscript && transcriptContent) payloadToSend.transcriptContent = transcriptContent;
    if (processedData) {
      payloadToSend.llm_summary = {};
      Object.keys(selections).forEach(key => {
        if (key !== 'includeTranscript' && selections[key] && processedData[key]) payloadToSend.llm_summary[key] = processedData[key];
      });
    }
    onStageChat(payloadToSend, notification.id);
  };

  // ✅ Optimized Option Tile (Lower padding, smaller text)
  const OptionTile = ({ name, label, icon: Icon, checked, onChange }) => (
    <div
      onClick={() => onChange({ target: { name, checked: !checked } })}
      className={`cursor-pointer select-none flex items-center gap-2 px-2.5 py-1.5 rounded-md border transition-all duration-200 text-[10px] ${checked
        ? 'bg-[#5652CF]/10 border-[#5652CF]/40 text-blue-100 shadow-[0_0_15px_-3px_rgba(59,130,246,0.15)]'
        : 'bg-zinc-800/40 border-zinc-700/50 text-zinc-400 hover:bg-zinc-800 hover:border-zinc-600 hover:text-zinc-300'
        }`}
    >
      <div className={`w-3.5 h-3.5 rounded-[3px] border flex items-center justify-center transition-colors ${checked ? 'bg-[#5652CF] border-[#5652CF]' : 'bg-transparent border-zinc-600 group-hover:border-zinc-500'}`}>
        {checked && <Check className="w-3 h-3 text-white stroke-[3px]" />}
      </div>
      <div className="flex items-center gap-1.5 min-w-0">
        {Icon && <Icon className={`w-3 h-3 ${checked ? 'text-blue-400' : 'text-zinc-500 group-hover:text-zinc-400'}`} />}
        <span className="font-medium truncate">{label}</span>
      </div>
    </div>
  );

  return (
    <div className="px-4 py-3 bg-[#121214] border-t border-zinc-800/50 flex flex-col md:flex-row items-center gap-4 justify-between z-20 relative shadow-[0_-5px_20px_rgba(0,0,0,0.4)]">
      {(processedData || transcriptContent) && (
        <div className="flex flex-wrap justify-center flex-1 gap-2 md:justify-start">
          {transcriptContent && <OptionTile icon={FileText} name="includeTranscript" label="Full Transcript" checked={selections.includeTranscript} onChange={handleSelectionChange} />}
          {processedData?.overall_summary && <OptionTile icon={AlignLeft} name="overall_summary" label="Summary" checked={selections.overall_summary} onChange={handleSelectionChange} />}
          {processedData?.member_wise_updates && <OptionTile icon={Users} name="member_wise_updates" label="Member Updates" checked={selections.member_wise_updates} onChange={handleSelectionChange} />}
          {processedData?.task_wise_summary && <OptionTile icon={CheckSquare} name="task_wise_summary" label="Task Updates" checked={selections.task_wise_summary} onChange={handleSelectionChange} />}
          {processedData?.unique_blockers && <OptionTile icon={AlertOctagon} name="unique_blockers" label="Blockers" checked={selections.unique_blockers} onChange={handleSelectionChange} />}
          {processedData?.extracted_action_items && <OptionTile icon={ListChecks} name="extracted_action_items" label="Action Items" checked={selections.extracted_action_items} onChange={handleSelectionChange} />}
        </div>
      )}
      <button onClick={handleSubmit} disabled={isBusy} className="w-full md:w-auto flex items-center justify-center gap-2 px-5 py-2.5 text-xs font-semibold text-white transition-all bg-zinc-900 border border-zinc-700 rounded-lg  disabled:bg-zinc-600 disabled:from-zinc-600 disabled:to-zinc-600 disabled:cursor-not-allowed hover:from-[#5652CF] hover:to-blue-400 hover:shadow-[#5652CF]/25 active:scale-95">
        <MessageSquarePlus className="w-4 h-4" /> Chat with Agent
      </button>
    </div>
  );
};

// ✅ BEAUTIFIED & COMPACTED: Teams Meeting Detail
const TeamsMeetingNotificationDetail = ({ notification, scrollContainerRef, onClose, onStageChat }) => {
  const { id, transcript_status } = notification || {};
  const [status, setStatus] = useState('idle');
  const [transcriptStatus, setTranscriptStatus] = useState(notification?.transcript_status || 'idle');
  const [processedData, setProcessedData] = useState(notification?.llm_summary || null);
  const [transcriptContent, setTranscriptContent] = useState(notification?.transcriptContent || null);
  const { updateNotification } = useNotifications();
  const { userGoogleId: accessToken } = DataState();
  const [isAtBottom, setIsAtBottom] = useState(true);
  const footerRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!notification) return;
    setStatus('loading');
    try {
      let fullUpdatedNotification;
      if (notification.user_provided_meeting) {
        fullUpdatedNotification = await getUserConfiguredMeetingDetails(accessToken, notification.id);
      } else {
        fullUpdatedNotification = await getMeetingDetails(accessToken, notification.id);
      }
      setProcessedData(fullUpdatedNotification.llm_summary || null);
      setTranscriptContent(fullUpdatedNotification.transcriptContent || null);
      setTranscriptStatus(fullUpdatedNotification.transcript_status);
      setStatus('idle');
    } catch (err) { console.error("Failed", err); setStatus('failed'); }
  }, [notification, accessToken]);

  useEffect(() => {
    if (!notification) return;
    setTranscriptStatus(notification.transcript_status || 'idle');
    setTranscriptContent(notification.transcriptContent || null);
    setProcessedData(notification.llm_summary || null);
    const shouldFetch = ['processed', 'reprocessing', 'no_transcript'].includes(notification.transcript_status);
    if (!shouldFetch) return;
    fetchData();
  }, [notification]);

  useEffect(() => {
    const scroller = scrollContainerRef.current;
    if (!scroller) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scroller;
      if (scrollHeight - scrollTop - clientHeight < 20) setIsAtBottom(true); else setIsAtBottom(false);
    };
    handleScroll();
    scroller.addEventListener('scroll', handleScroll);
    return () => scroller.removeEventListener('scroll', handleScroll);
  }, [scrollContainerRef, notification]);

  const scrollToFooter = () => { footerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); };

  const copyHelpers = {
    formatMemberUpdatesForCopy: (updates) => { if (!updates) return ""; return "Member Updates:\n" + updates.map(upd => `\nUser: ${upd.speaker_name} (${upd.role})\n- Done: ${upd.completed_tasks_summary}\n- Plan: ${upd.planned_tasks_summary}\n- Blockers: ${upd.blockers_text?.join(', ') || 'None'}`).join("\n"); },
    formatTaskUpdatesForCopy: (tasks) => { if (!tasks) return ""; return "Task Updates:\n" + tasks.map(task => `\nTask: ${task.issue_key}\n- Summary: ${task.consolidated_summary}\n- Status Hint: ${task.status_update_hint || 'N/A'}\n- Dependencies: ${task.dependencies?.map(d => d.description).join(', ') || 'None'}`).join("\n"); },
    formatBlockersForCopy: (blockers) => { if (!blockers) return ""; return "Unique Blockers:\n" + blockers.map(b => `- ${b.representative_description}`).join("\n"); }
  };

  const LoadingState = ({ text }) => (
    <div className="flex flex-col items-center justify-center py-20 text-center text-zinc-500">
      <Loader2 className="w-6 h-6 mb-3 text-[#5652CF] animate-spin" />
      <p className="text-sm font-semibold text-zinc-300">{text}</p>
      <p className="text-[10px]">Fetching latest data...</p>
    </div>
  );
  const NoTranscriptState = () => (<div className="flex flex-col items-center justify-center py-20 text-center text-zinc-500"><FileX className="w-8 h-8 mb-3 opacity-50" /><p className="text-sm font-semibold text-zinc-300">No Transcript</p><p className="text-[10px]">Recording data unavailable.</p></div>);
  const FailedState = () => (<div className="flex flex-col items-center justify-center py-20 text-center text-red-400"><AlertCircle className="w-8 h-8 mb-3" /><p className="text-sm font-semibold">Processing Failed</p><p className="text-[10px] text-zinc-500">Could not retrieve data.</p></div>);

  const renderContent = () => {
    if (status === 'loading') return <LoadingState text="Updating..." />;
    if (status === 'failed') return <FailedState />;
    switch (transcriptStatus) {
      case 'processing': case 'reprocessing': return <LoadingState text="Processing Transcript..." />;
      case 'no_transcript': return <NoTranscriptState />;
      case 'failed': return <FailedState />;
      case 'processed':
        return processedData ? (
          <div className="px-5 pt-3 pb-5">
            <ProcessedContent data={processedData} copyHelpers={copyHelpers} />
          </div>
        ) : (<div className="flex flex-col items-center justify-center py-20 text-center text-zinc-500"><Info className="w-6 h-6 mb-3 text-blue-400" /><p className="text-sm font-semibold text-zinc-400">Data Pending</p></div>);
      default: return <div className="p-8 text-sm text-center text-zinc-500">Status unknown. Refresh to check.</div>;
    }
  };

  if (!notification) return null;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Top Hero Section - Compacted & Pinned */}
      <div className="relative px-5 py-3 bg-[#121214] border-b border-zinc-800 shrink-0 z-10 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="flex-1 min-w-0 mr-4">
            <h2 className="text-base font-bold leading-tight truncate text-zinc-100">{notification.subject || "Untitled Meeting"}</h2>
          </div>
          <button onClick={fetchData} disabled={status === 'loading' || transcriptStatus.includes('processing')} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-400 text-[10px] font-semibold hover:bg-zinc-700 hover:text-zinc-100 transition-all shadow-sm shrink-0">
            <RefreshCw className={`h-3 w-3 ${status === 'loading' || transcriptStatus.includes('processing') ? 'animate-spin' : ''}`} />
            {status === 'loading' ? 'Refreshing' : 'Refresh'}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-[11px] text-zinc-400">
          <div className="flex items-center gap-1.5 min-w-0">
            <User className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
            <span className="truncate max-w-[150px]">{notification.organizer?.emailAddress?.name || notification.organizer?.emailAddress?.address || "Unknown"}</span>
          </div>
          <div className="w-px h-3 bg-zinc-700/50"></div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
            <span className="font-mono">
              {notification.start?.dateTime && notification.end?.dateTime ? `${parseUTCDateString(notification.start.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${parseUTCDateString(notification.end.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : "N/A"}
            </span>
          </div>
          <div className="w-px h-3 bg-zinc-700/50"></div>
          <div className="flex items-center gap-1.5 max-w-full overflow-hidden">
            <Users className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
            <span className="truncate">{notification.attendees?.map(p => p.emailAddress?.name || p.emailAddress?.address || "Unknown").join(', ') || "No Attendees"}</span>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area - Only this part scrolls */}
      <div ref={scrollContainerRef} className="relative flex-1 overflow-y-auto custom-scrollbar bg-zinc-900/20">
        {renderContent()}
        {!isAtBottom && status !== "loading" && (
          <button onClick={scrollToFooter} className="sticky z-20 p-2 text-white transition-all duration-300 -translate-x-1/2 bg-blue-600 rounded-full shadow-lg bottom-4 left-1/2 shadow-blue-600/30 hover:bg-[#5652CF] hover:scale-105 animate-bounce-slow">
            <ChevronDown className="w-4 h-4" />
          </button>
        )}
        <div ref={footerRef} />
      </div>

      <div className="relative z-10 shrink-0">
        <MeetingSubmissionControls notification={notification} processedData={processedData} transcriptContent={transcriptContent} onClose={onClose} onStageChat={onStageChat} />
      </div>
    </div>
  );
};

const NotificationChip = ({ notification, onClick, onClose }) => {
  const app = notification.source_type === 'listener' ? notification.listener_data.app : 'agent';
  return (
    <div className="relative group bg-zinc-800/90 backdrop-blur-sm border border-zinc-700/80 rounded-full flex items-center gap-2 p-1.5 pr-3 text-xs text-zinc-300 shadow-lg shadow-black/20 animate-fade-in-fast cursor-pointer hover:bg-zinc-700 transition-colors">
      <img src={appImages[app]} alt={app} className="w-5 h-5 rounded-full" />
      <button onClick={onClick} className="flex-1 text-left truncate max-w-[180px]"><span className="font-medium">{notification.event_name}</span></button>
      <button onClick={onClose} className="absolute -top-1.5 -right-1.5 bg-zinc-600 hover:bg-red-500 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-all duration-200 z-10 shadow-sm"><XIcon className="w-3 h-3 text-zinc-200" /></button>
    </div>
  );
};

const ListenerNotificationFooter = ({ notification, onClose, onStageChat }) => {
  const { isBusy } = DataState();

  const handleChatStart = () => {
    if (isBusy) return;
    onStageChat(notification, notification.id);
  };

  return (
    <div className="flex items-center justify-end w-full gap-4">
      <span className="mr-auto text-xs text-zinc-500 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" /> Action needed?</span>
      <button onClick={handleChatStart} disabled={isBusy} className="flex items-center gap-2 px-5 py-2 text-xs font-semibold text-white transition-all bg-blue-600 border border-[#5652CF] rounded-lg shadow-lg shadow-blue-900/20 hover:bg-[#5652CF] disabled:opacity-50 disabled:cursor-not-allowed hover:-translate-y-0.5">
        <MessageSquarePlus className="w-4 h-4" /> Resolve with AI
      </button>
    </div>
  );
};

// ✅ BEAUTIFIED: Main Modal Structure
const InChatNotificationView = ({ notification, onClose, inChat = false, onStageChat }) => {
  const viewRef = useRef();
  const scrollContainerRef = useRef(null);
  const [approvalAction, setApprovalAction] = useState({ isReady: false, isLoading: false, submit: () => { } });
  useOnClickOutside(viewRef, onClose, !approvalAction.isLoading);

  useEffect(() => { setApprovalAction({ isReady: false, isLoading: false, submit: () => { } }); }, [notification]);
  if (!notification) return null;

  const isApproval = notification.source_type === 'workflow';
  let appType = 'agent';
  let title = notification.event_name || notification.subject;
  if (notification.source_type === 'meeting') { appType = 'microsoft_teams'; title = "Meeting Summary"; }
  else if (notification.source_type === 'listener') { appType = notification.listener_data?.app; title = notification.event_name; }
  else if (isApproval) { appType = 'workflow'; title = notification.event_name || "Approval Needed"; }

  const renderDetail = () => {
    if (isApproval) return <ApprovalNotificationDetail notification={notification} onClose={onClose} onReadyStateChange={setApprovalAction} />;
    if (notification.source_type === 'meeting') return <TeamsMeetingNotificationDetail notification={notification} scrollContainerRef={scrollContainerRef} onClose={onClose} onStageChat={onStageChat} />;
    if (notification.source_type === 'listener') {
      switch (notification.listener_data.app) {
        case 'jira': return <JiraNotificationDetail notification={notification} />;
        case 'gmail': return <GmailNotificationDetail notification={notification} />;
        case 'zoho_crm': return <ZohoCrmNotificationDetail notification={notification} />;
        case 'slack': return <SlackNotificationDetail notification={notification} />;
        default: return <div className="p-8 text-center text-zinc-500">No detailed view available.</div>;
      }
    }
    return <div className="p-8 text-center text-zinc-500">Unknown type.</div>;
  };

  const renderFooter = () => {
    if (isApproval) {
      const nodeType = notification?.workflow_data?.node_type;
      if (nodeType === 'human') return (
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-zinc-800 bg-zinc-900 rounded-b-xl shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold transition-colors border rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border-zinc-700">Deny</button>
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold text-white transition-colors rounded-lg shadow-lg bg-emerald-600 hover:bg-emerald-500 shadow-emerald-900/20">Approve</button>
        </div>
      );
      if (nodeType === 'action') return (
        <div className="flex items-center justify-end px-6 py-4 border-t border-zinc-800 bg-zinc-900 rounded-b-xl shrink-0">
          <button onClick={approvalAction.submit} disabled={!approvalAction.isReady || approvalAction.isLoading} className="flex items-center gap-2 px-6 py-2.5 text-xs font-semibold text-white transition-all bg-gradient-to-r from-blue-600 to-[#5652CF] border border-[#5652CF] rounded-lg shadow-lg hover:shadow-[#5652CF]/25 disabled:bg-zinc-800 disabled:border-zinc-700 disabled:text-zinc-500 disabled:from-zinc-800 disabled:to-zinc-800 disabled:cursor-not-allowed">
            {approvalAction.isLoading ? "Processing..." : <><ThumbsUp className="w-4 h-4" /> Confirm Decisions</>}
          </button>
        </div>
      );
    }
    if (notification.source_type === 'listener') return <div className="px-6 py-4 border-t border-zinc-800 bg-zinc-900 rounded-b-xl shrink-0"><ListenerNotificationFooter notification={notification} onClose={onClose} onStageChat={onStageChat} /></div>;
    return null;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 duration-300 bg-black/80 backdrop-blur-[2px] animate-in fade-in">
      <div ref={viewRef} className="relative w-full max-w-4xl bg-[#121214] border border-zinc-800 rounded-2xl shadow-2xl flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-300 ring-1 ring-white/5 overflow-hidden">

        {/* Unified Modal Header - Pinned */}
        <div className="sticky top-0 z-20 flex items-center justify-between px-5 py-3 border-b border-zinc-800 bg-[#121214] backdrop-blur-md shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-zinc-800/60 rounded-xl border border-zinc-700/50 shadow-inner">
              <img src={appImages[appType] || appImages.agent} alt={appType} className="object-contain w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold leading-none tracking-tight text-zinc-100">{title}</h3>
              <p className="text-[10px] text-zinc-500 mt-0.5 capitalize font-medium flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/80 inline-block"></span>
                {appType?.replace(/_/g, " ")} Notification
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 transition-all duration-200 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content Body - Flexible & Scrollable */}
        <div ref={scrollContainerRef} className="relative flex flex-col flex-1 w-full min-h-0">
          {renderDetail()}
        </div>

        {/* Footer - Pinned */}
        {renderFooter()}
      </div>
    </div>
  );
};

// ===================================================================================
// --- MAIN UI COMPONENT ---
// ===================================================================================

const Ui = () => {
  const {
    currentTaskId,
    setJustSavedFiles,
    sendTaskLoading,
    taskInput,
    setTaskInput,
    creatingTask,
    setCreatingTask, // Ensure this is destructured
    userGoogleId,
    handleSubmitTask,
    userInfo,
    isBusy,
    toggleSidebar,
    setToggleSidebar,
    rightSidebarWidth, setRightSidebarWidth,
  } = DataState();

  const {
    openNotifications,
    closeNotificationInContext,
    activeNotificationId,
    setActiveNotificationId,
    collapsedNotificationIds,
    setCollapsedNotificationIds,
    createNewTaskForNotification,
  } = useNotifications();

  // --- NEW STATE: Pending Context ---
  const [pendingContext, setPendingContext] = useState(null);
  const isStagingRef = useRef(false);

  const [showIntegrations, setShowIntegrations] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showIntegrationTutorial, setShowIntegrationTutorial] = useState(false);
  const [tempImages, setTempImages] = useState([]);
  const [justUploadedImages, setJustUploadedImages] = useState(false);
  const isResizing = useRef(false);
  const resizableContainerRef = useRef(null);

  const handleCollapse = useCallback(() => {
    if (activeNotificationId) {
      setCollapsedNotificationIds(prev => new Set(prev).add(activeNotificationId));
      setActiveNotificationId(null);
    }
  }, [activeNotificationId, setActiveNotificationId, setCollapsedNotificationIds]);

  const handleChipClick = (notificationId) => {
    if (activeNotificationId && activeNotificationId !== notificationId) {
      setCollapsedNotificationIds(prev => new Set(prev).add(activeNotificationId));
    }
    setActiveNotificationId(notificationId);
    setCollapsedNotificationIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(notificationId);
      return newSet;
    });
  };

  const handleCloseNotification = (e, notificationId) => {
    e.stopPropagation();
    closeNotificationInContext(notificationId);
  };

  const activeNotification = useMemo(() => {
    if (!openNotifications || !activeNotificationId) return null;
    return openNotifications.find(n => n.id === activeNotificationId) || null;
  }, [openNotifications, activeNotificationId]);

  // ✅ Auto-close notification logic with staging check
  useEffect(() => {
    if (isStagingRef.current) {
      isStagingRef.current = false;
      return;
    }
    setActiveNotificationId(null);
    setCollapsedNotificationIds(new Set());
  }, [currentTaskId, creatingTask, setActiveNotificationId, setCollapsedNotificationIds]);

  // ✅ NEW: Handle "Chat with Agent" click (Staging Phase)
  const handleStageChat = (payload, notificationId) => {
    setPendingContext(payload);
    isStagingRef.current = true;
    setCollapsedNotificationIds(prev => new Set(prev).add(notificationId));
    setActiveNotificationId(null); // Close the modal
    if (setCreatingTask) setCreatingTask(true);
  };

  // ✅ NEW: Wrapper for Sending Message
  const handleSmartSend = async (messageParam, attachments, explicitInput = null) => {

    // Choose the explicitly passed input sequence if provided
    const message = explicitInput !== null ? explicitInput : messageParam;

    if (pendingContext) {
      // Send the pending context along with the user message
      await createNewTaskForNotification(pendingContext, message);
      setPendingContext(null); // Clear pending state
      setCollapsedNotificationIds(new Set()); // Now we can clear the chips
    } else {
      // Standard flow
      await handleSubmitTask(message, explicitInput);
    }
  };

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing.current || !resizableContainerRef.current) return;
    const containerRect = resizableContainerRef.current.getBoundingClientRect();
    const handleWidth = 8;
    const newWidth = containerRect.right - e.clientX - (handleWidth / 2);
    if (newWidth >= 350 && newWidth <= 550) {
      setRightSidebarWidth(newWidth);
    }
  }, [setRightSidebarWidth]);

  const handleMouseUp = useCallback(() => {
    isResizing.current = false;
    document.body.style.cursor = 'default';
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragCounter = useRef(0);
  const processAndUploadFiles = async (fileList) => {
    const filesToProcess = Array.from(fileList);
    if (filesToProcess.length === 0) return;
    if (filesToProcess.length > 10) {
      toast.error("You can upload a maximum of 10 files at a time.");
      return;
    }
    const imagesToUpload = filesToProcess.filter((file) => file.type.startsWith("image/"));
    const otherFilesToUpload = filesToProcess.filter((file) => !file.type.startsWith("image/"));
    if (imagesToUpload.length > 0) {
      const newTempImages = imagesToUpload.map((file) => ({
        id: Date.now() + Math.random(),
        url: URL.createObjectURL(file),
        uploading: true,
      }));
      setTempImages(newTempImages);
    }
    try {
      if (imagesToUpload.length > 0) {
        const imageData = await uploadImages(userGoogleId, currentTaskId, imagesToUpload);
        setTempImages([]);
        if (imageData.error) {
          toast.error("Failed to upload some images.");
        } else {
          setJustUploadedImages(true);
        }
      }
      if (otherFilesToUpload.length > 0) {
        const fileData = await uploadFiles(userGoogleId, currentTaskId, otherFilesToUpload);
        if (fileData && !fileData.error) {
          setJustSavedFiles(true);
        } else {
          toast.error("Failed to upload some files.");
        }
      }
      toast.success("Files uploaded successfully!");
    } catch (error) {
      console.error("Error processing files:", error);
      toast.error("An error occurred while uploading files.");
    } finally {
      setTempImages([]);
    }
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
      setIsDraggingOver(true);
    }
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDraggingOver(false);
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    dragCounter.current = 0;
    if (!sendTaskLoading) {
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        processAndUploadFiles(e.dataTransfer.files);
      } else if (e.dataTransfer.types.includes('text/plain')) {
        const draggedText = e.dataTransfer.getData("text/plain");
      }
    }
  };
  const handleMobileSidebarToggle = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };
  const handleDesktopToggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setToggleSidebar(!toggleSidebar);
  };
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && mobileSidebarOpen) {
        setMobileSidebarOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mobileSidebarOpen]);


  const [showFeedback, setShowFeedback] = useState(false);

  const handleFeedbackSubmit = (data) => {
    console.log('Feedback submitted:', data);
    // Send to your backend
    setShowFeedback(false);
  };



  const [morningBriefing, setMorningBriefing] = useState(false);

  return (
    <div className="relative h-screen w-screen max-h-[100svh] bg-3 flex flex-col md:flex-row p-2 md:p-2 gap-2 md:gap-2 text-p-text overflow-hidden">
      <div className={`${toggleSidebar ? "hidden md:block md:w-16" : "hidden md:block md:w-56"} h-full relative overflow-hidden transform transition-all duration-150 flex-shrink-0 border border-zinc-700 rounded-xl md:rounded-2xl`}>
        {/* MODIFIED: REMOVED button from here so it doesn't overlap */}
        <Sidebar toggleSidebar={toggleSidebar} isOpen={false} onToggle={() => { }} />
      </div>
      <div className="md:hidden">
        <Sidebar toggleSidebar={false} isOpen={mobileSidebarOpen} onToggle={handleMobileSidebarToggle} />
      </div>
      <div ref={resizableContainerRef} className="flex flex-1 h-full min-w-0">
        <div className={`relative flex-col flex-1 min-h-0 gap-4 pt-2 pb-3 overflow-hidden border bg-1 rounded-xl md:rounded-2xl md:pt-4 md:pb-4 md:gap-7 border-zinc-700  ${morningBriefing ? "hidden" : "flex"}`}>
          <div
            className="absolute z-50 flex flex-col items-start gap-2 transition-all duration-300 ease-in-out left-4"
            style={{ top: toggleSidebar ? '2.5rem' : '1rem' }}
          >
            {(openNotifications || [])
              .filter(n => collapsedNotificationIds?.has(n.id))
              .map(n => (
                <NotificationChip
                  key={n.id}
                  notification={n}
                  onClick={() => handleChipClick(n.id)}
                  onClose={(e) => handleCloseNotification(e, n.id)}
                />
              ))}

            {/* Standalone chip for email "Chat with Agent" — no notification context needed */}
            {pendingContext?._synthetic && (
              <NotificationChip
                notification={pendingContext}
                onClick={() => {}}
                onClose={(e) => {
                  e.stopPropagation();
                  setPendingContext(null);
                  if (setCreatingTask) setCreatingTask(false);
                }}
              />
            )}
          </div>

          <InChatNotificationView notification={activeNotification} onClose={handleCollapse} onStageChat={handleStageChat} />
          <div className="z-30">
            <Tabbar absolute={true} />
          </div>
          <button onClick={(e) => { e.stopPropagation(); setShowIntegrations(true); }} className="integration-button absolute top-2 right-2 md:right-4 px-2 md:px-2.5 py-1 md:py-1.5 rounded-full flex items-center gap-1 md:gap-2 border border-zinc-500 text-xs z-20 hover:bg-white hover:bg-opacity-10 transition-colors">
            <FiLink className="text-[#5B58DB] text-sm" />
            <span className="hidden sm:inline">Integrations</span>
          </button>

          {/* MODIFIED: Added button here with NO conditions, so it's always visible in the main content area */}
          <button className="absolute z-10 hidden p-1 text-lg transition-colors rounded-lg top-2 left-2 md:text-xl md:block hover:bg-white hover:bg-opacity-10" onClick={handleDesktopToggle}>
            <BiSidebar />
          </button>

          <div className="flex flex-col flex-1 max-h-full min-h-0 mt-12 md:mt-0" onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop}>
            {creatingTask ? (
              <div className="flex flex-col justify-center w-full h-full max-w-4xl gap-3 px-1 mx-auto">
                <div className="flex flex-col items-center justify-center flex-1 min-h-0 gap-6 px-2 text-center md:gap-10">
                  <OqlousCTA />
                </div>
                <div className="flex-shrink-0 pb-2 mt-auto md:pb-0">
                  <AutoResizableTextarea
                    setInput={setTaskInput}
                    input={taskInput}
                    handleSend={(messageToSend) => {
                      if (messageToSend) {
                        setTaskInput(messageToSend);
                      }
                      handleSmartSend(null, null, messageToSend);
                    }}
                    isDraggingOver={isDraggingOver}
                    tempImages={tempImages}
                    justUploadedImages={justUploadedImages}
                    setJustUploadedImages={setJustUploadedImages}
                    processAndUploadFiles={processAndUploadFiles}
                    setTempImages={setTempImages}
                    newChat={creatingTask}
                  />
                </div>
              </div>

            ) : (
              <div className="flex-1 min-h-0 overflow-hidden">
                <ChatList tempImages={tempImages} isDraggingOver={isDraggingOver} setTempImages={setTempImages} justUploadedImages={justUploadedImages} setJustUploadedImages={setJustUploadedImages} processAndUploadFiles={processAndUploadFiles} />
              </div>
            )}
          </div>
        </div>
        <div className={`items-center justify-center flex-shrink-0 hidden w-2 cursor-col-resize group ${morningBriefing ? "hidden" : "md:flex"}`} onMouseDown={handleMouseDown}>
          <div className="w-0.5 h-12 bg-zinc-700 group-hover:bg-[#5652CF] rounded-full transition-colors duration-200" />
        </div>
        {/* <div className="hidden h-full overflow-hidden md:block" style={{ width: `${rightSidebarWidth}px` }}>
          <RightSideActionTracker />
        </div> */}
        <div
          className={morningBriefing ? "flex-1 h-full overflow-hidden" : "hidden h-full overflow-hidden md:block"}
          style={morningBriefing ? {} : { width: `${rightSidebarWidth}px` }}
        >
          <RightSideActionTracker
            isMorningBriefing={morningBriefing}
            onMorningBriefing={setMorningBriefing}
            onStageChat={handleStageChat}
          />
        </div>

      </div>
      {showIntegrations && (
        <div className="fixed inset-0 z-50">
          <Integration onClose={() => setShowIntegrations(false)} user_google_id={userGoogleId} />
        </div>
      )}
      <IntegrationTutorial onIntegrationClick={() => setShowIntegrations(true)}></IntegrationTutorial>


      {/* <button
        onClick={() => setShowFeedback(true)}
        className="hidden md:block fixed bottom-6 right-6 bg-[#5652CF] hover:bg-[#5652CF]/90 text-white rounded-full p-4 shadow-lg transition-all z-40"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </button> */}

      {/* Feedback Modal */}
      {showFeedback && (
        <FeedbackComponent
          onClose={() => setShowFeedback(false)}
          onSubmit={handleFeedbackSubmit}
        />
      )}
    </div>
  );
};

export default Ui;
